import React, { useRef, useState, useEffect } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm        from '../../components/RSVPForm';
import Wishlist        from '../../components/Wishlist';
import YandexMap       from '../../components/YandexMap';
import Countdown       from '../../components/Countdown';
import FixedCTA        from '../../components/FixedCTA';
import useReveal       from '../../components/useReveal';
import Schedule        from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton  from '../../components/CalendarButton';
import BlockSubtitle   from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Rustic.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u;
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '??.??.????';
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map((n, i) => i < 2 ? String(n).padStart(2, '0') : String(n)).join('.');
};

/* Montserrat from Google + self-hosted Kudry Weird Headline. The Kudry
   @font-face is injected as a <style> tag at runtime (not via CSS import)
   so css-loader never tries to resolve the public-folder URL, and the
   chatbot's iframe preview — which clones <style> tags — inherits it. */
function useRusticFonts() {
  useEffect(() => {
    if (!document.getElementById('rstc-fonts')) {
      const link = document.createElement('link');
      link.id = 'rstc-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;700&display=swap';
      document.head.appendChild(link);
    }
    if (!document.getElementById('rstc-kudry')) {
      const style = document.createElement('style');
      style.id = 'rstc-kudry';
      style.textContent = `@font-face{font-family:'Kudry Weird Headline';`
        + `src:url('/templates/rustic/fonts/kudry-weird-headline.woff2') format('woff2'),`
        + `url('/templates/rustic/fonts/kudry-weird-headline.woff') format('woff');`
        + `font-weight:400;font-style:normal;font-display:swap;}`;
      document.head.appendChild(style);
    }
  }, []);
}

/* ── Class maps for shared components ─────────────────────── */
const CD_CLASSES    = { root: 'rstc-cd',    cell: 'rstc-cd-cell', num: 'rstc-cd-num', label: 'rstc-cd-lbl' };
const SCHED_CLASSES = { root: 'rstc-sched', row: 'rstc-sched-row', rowBorder: 'rstc-sched-row--border', time: 'rstc-sched-time', info: 'rstc-sched-info', name: 'rstc-sched-name', desc: 'rstc-sched-desc' };
const DRESS_CLASSES = { wrap: 'rstc-swatches', swatchWrap: 'rstc-swatch-wrap', swatch: 'rstc-swatch', swatchLabel: 'rstc-swatch-lbl', photos: 'rstc-dc-photos', photo: 'rstc-dc-photo' };
const RSVP_CLASSES = {
  form:    'rstc-form',
  tabs:    'rstc-rsvp-tabs',
  tab:     'rstc-rsvp-tab',
  tabSel:  'rstc-rsvp-tab--sel',
  field:   'rstc-form-field',
  label:   'rstc-form-lbl',
  input:   'rstc-form-input',
  submit:  'rstc-btn rstc-btn--primary rstc-btn--full',
  success: 'rstc-form-ok',
  error:   'rstc-form-err',
  chip:    'rstc-chip',
  chipSel: 'rstc-chip--sel',
  chips:   'rstc-form-chips',
};
const WISHLIST_CLASSES = {
  list:        'rstc-wish',
  item:        'rstc-wish-item',
  itemBorder:  'rstc-wish-item--border',
  row:         'rstc-wish-row',
  name:        'rstc-wish-name',
  price:       'rstc-wish-price',
  btn:         'rstc-btn rstc-btn--outline rstc-btn--full',
  btnReserved: 'rstc-btn--res',
};
const WISHLIST_MODAL_CLASSES = {
  bg:         'rstc-wish-modal-bg',
  modal:      'rstc-wish-modal',
  title:      'rstc-wish-modal-title',
  subtitle:   'rstc-wish-modal-sub',
  input:      'rstc-form-input',
  error:      'rstc-form-err',
  btns:       'rstc-wish-modal-btns',
  btnCancel:  'rstc-btn rstc-btn--outline',
  btnConfirm: 'rstc-btn rstc-btn--primary',
};

const R = '/templates/rustic';
const Diamond = () => <span className="rstc-div" aria-hidden="true">✦</span>;

/* ══ Main Template ═════════════════════════════════════════════ */
export default function Rustic({ project, design, isPreview }) {
  const pageRef  = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.rstc-reveal', inClass: 'rstc-in' });
  useRusticFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};

  /* Colours — defaults match the mockup (olive accent on dark forest).
     `primary` = olive accent, `secondary` = the dark background. */
  const accent = d.palette?.primary   || p.palette?.primary   || '#81964A';
  const bg     = d.palette?.secondary || p.palette?.secondary || '#212C1C';

  /* Blocks */
  const blocks = p.blocks || ['menu','map','schedule','palette','rsvp','survey','message','wishlist','countdown','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');

  /* Text */
  const bride       = p.bride || '';
  const groom       = p.groom || '';
  const tagline     = p.block_content?.header?.subtitle || '';
  const invText     = p.invitation_text || p.block_content?.message?.text || '';
  const invTitle    = p.block_content?.message?.title || 'Дорогие наши!';
  const footerTitle = p.block_content?.footer?.quote || 'Спасибо, что вы с нами';

  /* Photos */
  const photos        = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto     = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;
  const dcPhotos      = p.dresscode_photos || [];
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date */
  const dateStr = fmtDateDots(p.wedding_date);

  /* Data */
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = p.venues || [];
  const swatches = p.dresscode_palette || [];
  const wishlist = p.wishlist || [];

  /* Block content titles */
  const bc = p.block_content || {};
  const titleLocation = bc.map?.title       || 'Локация';
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`rstc-page${inPreview ? ' rstc-page--preview' : ''}`} ref={pageRef}
      style={{ '--rstc-accent': accent, '--rstc-bg': bg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* Navigation */}
      {HAS_MENU && (
        <nav className="rstc-nav">
          {blocks.includes('schedule') && <a href="#rstc-schedule">{bc.menu?.schedule || 'Программа'}</a>}
          {blocks.includes('map')      && <a href="#rstc-location">{bc.menu?.location || 'Локация'}</a>}
          {blocks.includes('palette')  && <a href="#rstc-dresscode">{bc.menu?.dresscode || 'Дресс-код'}</a>}
          {blocks.includes('rsvp')     && <a href="#rstc-rsvp">{bc.menu?.rsvp || 'Анкета'}</a>}
        </nav>
      )}

      {/* Hero — badge + names + lace-framed photo */}
      <section className="rstc-hero rstc-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        {tagline && <span className="rstc-badge">{tagline}</span>}
        <div className="rstc-hero-names">
          {bride && <span className="rstc-hero-name">{bride}</span>}
          <span className="rstc-hero-amp">{bc.header?.amp || 'и'}</span>
          {groom && <span className="rstc-hero-name">{groom}</span>}
        </div>
        {heroPhoto && (
          <div className="rstc-hero-frame">
            <div className="rstc-hero-photo">
              <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
            </div>
            <img className="rstc-lace" src={`${R}/lace.png`} alt="" aria-hidden="true" />
          </div>
        )}
      </section>

      {/* Invitation + date (overlapping panels) */}
      <section className="rstc-intro rstc-reveal">
        <div className="rstc-invite">
          <h2 className="rstc-invite-title">{invTitle}</h2>
          <Diamond />
          <div className="rstc-invite-body">
            {invText
              ? invText.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
              : null}
          </div>
        </div>
        <div className="rstc-date-panel">
          <p className="rstc-date">{dateStr}</p>
          {HAS_CALENDAR && (
            <CalendarButton
              bride={bride}
              groom={groom}
              date={p.wedding_date}
              time={p.wedding_time}
              venues={p.venues}
              address={p.wedding_address}
              className="rstc-cal-btn">
              {calBtn}
            </CalendarButton>
          )}
        </div>
      </section>

      {/* Dynamic blocks — Figma order */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="rstc-pad rstc-map-sect rstc-reveal" id="rstc-location">
                  <h2 className="rstc-h">{titleLocation}</h2>
                  <Diamond />
                  <BlockSubtitle text={bc.map?.subtitle} className="rstc-p" />
                  <p className="rstc-p">
                    {venues.filter(v => (v.name || '').trim() || (v.address || '').trim()).length > 0
                      ? venues.filter(v => (v.name || '').trim() || (v.address || '').trim()).flatMap((v, i, arr) => {
                          const line = <span key={`a${i}`} style={{ display: 'block' }}>{v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}</span>;
                          return i < arr.length - 1
                            ? [line, <span key={`d${i}`} style={{ display: 'block', textAlign: 'center', opacity: 0.5, margin: '6px 0' }}>•</span>]
                            : [line];
                        })
                      : (p.wedding_address || '')}
                  </p>
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="rstc-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#greenIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="rstc-pad rstc-sched-sect rstc-reveal" id="rstc-schedule">
                  <h2 className="rstc-h rstc-h--left">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="rstc-p rstc-p--left" />
                  <Schedule items={schedule} classes={SCHED_CLASSES} />
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="rstc-dress-sect rstc-reveal" id="rstc-dresscode">
                  <div className="rstc-dress-card">
                    <h2 className="rstc-h rstc-h--dark">{titleDress}</h2>
                    <Diamond />
                    <BlockSubtitle text={bc.palette?.subtitle} className="rstc-p rstc-p--dark" />
                    <DresscodePalette swatches={swatches} classes={DRESS_CLASSES} />
                  </div>
                  {dcPhotos.length > 0 && (
                    <div className="rstc-dc-stack">
                      {dcPhotos.slice(0, 4).map((ph, i) => (
                        <div key={i} className="rstc-dc-photo">
                          <img src={src(ph.url)} alt=""
                            style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="rstc-pad rstc-rsvp-sect rstc-reveal" id="rstc-rsvp">
                  <h2 className="rstc-h">{titleRsvp}</h2>
                  <Diamond />
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="rstc-p" />
                  <RSVPForm project={p} blocks={blocks} classes={RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="rstc-pad rstc-wish-sect rstc-reveal">
                  <h2 className="rstc-h">{titleWish}</h2>
                  <Diamond />
                  <BlockSubtitle text={bc.wishlist?.subtitle} className="rstc-p" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={WISHLIST_CLASSES}
                    modalClasses={WISHLIST_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="rstc-pad rstc-cd-sect rstc-reveal">
                  <h2 className="rstc-h">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle} className="rstc-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* Footer */}
      <footer className="rstc-footer rstc-reveal">
        {footerPhoto && (
          <div className="rstc-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="rstc-footer-panel">
          <h2 className="rstc-footer-title">{footerTitle}</h2>
        </div>
      </footer>

      {/* Floating RSVP CTA */}
      {blocks.includes('rsvp') && (
        <FixedCTA
          href="#rstc-rsvp"
          targetId="rstc-rsvp"
          scrollRoot={pageRef}
          className="rstc-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
